<template>
  <form @submit.prevent="saveValue" ref="form">
    <div class="modal" tabindex="-1" role="dialog" backdrop="static" id="addVirtualSensorValueModal" @keyup.esc="onEsc($event)">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header header">
            <h3 class="modal-title">{{ $t('addVirtualSensorValueModal.title') }}</h3>
            <button type="button" class="btn-close btn-close-white" @click="close" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="form">
              <div v-show="errorMessage" class="alert alert-danger failed">{{ errorMessage }}</div>
              <div class="form-group">
                <label>{{ $t('addVirtualSensorValueModal.sensorName') }}</label>
                <div class="blue-text">{{ selectedSensor.name }}</div>
              </div>
              <div class="form-group">
                <label for="virtualValueInput">
                  {{ primaryLabel }}
                  <span v-if="primary.valuetype >= 0">&nbsp;/&nbsp; {{ displayUnitForValueType(primary.valuetype) }}</span>
                </label>
                <input type="text" inputmode="decimal" class="form-control" id="virtualValueInput" v-model.trim="primary.value" />
                <div class="invalid-feedback" style="display: block;" v-if="v$.primary.value.$dirty && v$.primary.value.$error">
                  <div v-if="v$.primary.value.required.$invalid">{{ $t('addVirtualSensorValueModal.valueRequired') }}</div>
                  <div v-else-if="v$.primary.value.decimal.$invalid">{{ $t('addVirtualSensorValueModal.valueDecimal') }}</div>
                </div>
              </div>
              <div class="form-group" v-if="hasSecondary">
                <label for="virtualSecondaryValueInput">
                  {{ secondaryLabel }}
                  <span v-if="secondary.valuetype >= 0">&nbsp;/&nbsp; {{ displayUnitForValueType(secondary.valuetype) }}</span>
                </label>
                <input type="text" inputmode="decimal" class="form-control" id="virtualSecondaryValueInput" v-model.trim="secondary.value" />
                <div class="invalid-feedback" style="display: block;" v-if="v$.secondary.value.$dirty && v$.secondary.value.$error">
                  <div v-if="v$.secondary.value.required.$invalid">{{ $t('addVirtualSensorValueModal.valueRequired') }}</div>
                  <div v-else-if="v$.secondary.value.decimal.$invalid">{{ $t('addVirtualSensorValueModal.valueDecimal') }}</div>
                </div>
              </div>
              <div class="form-group">
                <label>{{ $t('addVirtualSensorValueModal.readingInput') }}</label>
                <VueDatePicker v-model="recordedtime" model-type="iso" :auto-apply="true" />
                <div class="invalid-feedback" style="display: block;" v-if="invalidReading">
                  {{ $t('addVirtualSensorValueModal.readingError') }}
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="submit" class="btn btn-primary">{{ $t('addVirtualSensorValueModal.buttonAdd') }}</button>
            <button type="button" class="btn btn-secondary" @click="close">{{ $t('addVirtualSensorValueModal.close') }}</button>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import { Modal } from 'bootstrap'
import moment from 'moment'
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import deviceService from '@/services/cdcdevices'
import ModalViewMixin from '@/mixins/ModalViewMixin.js'
import { isDecimalInput, parseDecimalInput } from '@/utils/decimal-input.js'

// OBIS codes for electricity import/export totals. Frontend-only hint to give
// the user context next to the value-type label; the server doesn't care.
const OBIS_FOR_VALUETYPE = {
  9: '1.8.0',
  10: '2.8.0'
}

export default {
  setup () { return { v$: useVuelidate() } },
  name: 'AddVirtualSensorValueModal',
  mixins: [ModalViewMixin],
  props: {
    selectedSensor: { type: Object, required: true },
    firstGlancePair: { type: Object, required: true }
  },
  data () {
    return {
      primary: { systempart: null, valuetype: -1, value: null, factor: 0 },
      secondary: { systempart: null, valuetype: -1, value: null, factor: 0 },
      hasSecondary: false,
      recordedtime: new Date(),
      errorMessage: ''
    }
  },
  watch: {
    modalOpened () {
      const meta = this.parseSensorJson(this.selectedSensor)
      const primaryValueType = (typeof meta.primaryValueType === 'number') ? meta.primaryValueType : this.firstGlancePair.valuetype
      const secondaryValueType = (typeof meta.secondaryValueType === 'number') ? meta.secondaryValueType : null

      this.primary = {
        systempart: this.firstGlancePair.systempart,
        valuetype: primaryValueType,
        value: null,
        factor: 0
      }
      this.secondary = {
        systempart: this.firstGlancePair.systempart,
        valuetype: secondaryValueType != null ? secondaryValueType : -1,
        value: null,
        factor: 0
      }
      this.hasSecondary = secondaryValueType != null
      this.recordedtime = new Date()
      this.errorMessage = ''
      this.v$.$reset()
      this.$nextTick(() => {
        document.getElementById('virtualValueInput')?.focus()
      })
    }
  },
  validations () {
    const rules = { primary: { value: { required, decimal: isDecimalInput } } }
    if (this.hasSecondary) {
      rules.secondary = { value: { required, decimal: isDecimalInput } }
    } else {
      rules.secondary = { value: {} }
    }
    return rules
  },
  computed: {
    invalidReading () {
      return !this.recordedtime
    },
    primaryLabel () {
      const obis = OBIS_FOR_VALUETYPE[this.primary.valuetype]
      if (obis && this.hasSecondary) {
        // Only annotate with OBIS when there is a partner reading — otherwise
        // the OBIS code adds noise without value.
        return this.$t('addVirtualSensorValueModal.valueInputObis', { obis })
      }
      return this.$t('addVirtualSensorValueModal.valueInput')
    },
    secondaryLabel () {
      const obis = OBIS_FOR_VALUETYPE[this.secondary.valuetype]
      return obis
        ? this.$t('addVirtualSensorValueModal.valueInputObis', { obis })
        : this.$t('addVirtualSensorValueModal.valueInputSecondary')
    }
  },
  mounted () {
    document.getElementById('addVirtualSensorValueModal').addEventListener('shown.bs.modal', () => {
      document.getElementById('virtualValueInput')?.focus()
    })
  },
  methods: {
    parseSensorJson (sensor) {
      if (!sensor || !sensor.json || typeof sensor.json !== 'string') return {}
      try {
        const parsed = JSON.parse(sensor.json)
        return (parsed && typeof parsed === 'object') ? parsed : {}
      } catch (e) {
        return {}
      }
    },
    // The user enters readings in the unit shown on the meter (kWh for
    // electricity / heat, m³ for gas / water). The backend stores values
    // in their physical-unit baseline (Wh for valuetype 9/10), so the
    // factor encodes the kWh→Wh shift: stored = value × 10^factor.
    displayUnitForValueType (valueType) {
      switch (valueType) {
        case 9:
        case 10:
          return 'kWh'
        case 22:
          return '㎥'
        default:
          return this.localizedUnit(valueType)
      }
    },
    submitFactorForValueType (valueType) {
      switch (valueType) {
        case 9:
        case 10:
          return 3 // user typed kWh → backend baseline is Wh
        default:
          return 0
      }
    },
    saveValue () {
      this.v$.$touch()
      if (this.v$.$invalid || this.invalidReading) {
        return
      }
      const recordedtime = moment(this.recordedtime).unix()
      const primaryFactor = this.submitFactorForValueType(this.primary.valuetype)

      if (this.hasSecondary) {
        const secondaryFactor = this.submitFactorForValueType(this.secondary.valuetype)
        const payload = {
          recordedtime,
          vs: [
            { systempart: this.primary.systempart, valuetype: this.primary.valuetype, value: parseDecimalInput(this.primary.value), factor: primaryFactor },
            { systempart: this.secondary.systempart, valuetype: this.secondary.valuetype, value: parseDecimalInput(this.secondary.value), factor: secondaryFactor }
          ]
        }
        deviceService.addVirtualSensorValues(this.selectedSensor.id, payload).then(() => {
          this.close()
        }).catch(error => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
      } else {
        const payload = {
          cdcdeviceid: this.selectedSensor.cdcdeviceid,
          cdcsensorid: this.selectedSensor.id,
          systempart: this.primary.systempart,
          valuetype: this.primary.valuetype,
          value: parseDecimalInput(this.primary.value),
          factor: primaryFactor,
          recordedtime
        }
        deviceService.addVirtualSensorValue(payload).then(() => {
          this.close()
        }).catch(error => {
          this.errorMessage = this.localizedBackendMessage(error.message)
        })
      }
    },
    close () {
      this.v$.$reset()
      this.errorMessage = ''
      this.$refs.form.reset()
      this.emitClosed()
      Modal.getInstance(document.getElementById('addVirtualSensorValueModal'))?.hide()
    }
  }
}
</script>

<style lang="scss" scoped>
.modal {
  .modal-dialog { width: 400px; }
  .blue-text { color: #0069d9; font-weight: 700; }
}
</style>
